import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Card, Group, Stack, Text, Title } from "@mantine/core";
import React from 'react';

interface AdminPageLayoutProps {
  /** Page title */
  title: string;
  /** Optional subtitle/description */
  description?: string;
  /** Optional FontAwesome icon for the page header */
  icon?: IconDefinition;
  /** Page content */
  children: React.ReactNode;
  /** Optional actions/buttons for the header */
  actions?: React.ReactNode;
  /** Optional badge text (like "New", "Legacy") */
  badge?: string;
  /** Badge color */
  badgeColor?: string;
  /** Whether to show the card wrapper (default: true) */
  showCard?: boolean;
  /** Custom spacing for content */
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Standardized layout wrapper for all admin pages
 * Provides consistent spacing, typography, and structure
 */
const AdminPageLayout: React.FunctionComponent<AdminPageLayoutProps> = ({
  title,
  description,
  icon,
  children,
  actions,
  badge,
  badgeColor = "blue",
  showCard = true,
  spacing = "lg"
}) => {

  const pageHeader = (
    <Group justify="space-between" mb="md">
      <Group>
        {icon && (
          <FontAwesomeIcon
            icon={icon}
            style={{
              fontSize: '24px',
              color: 'var(--color-button-primary)'
            }}
          />
        )}
        <div>
          <Group align="center" gap="sm">
            <Title order={2} size="h3" fw={600}>
              {title}
            </Title>
            {badge && (
              <Badge color={badgeColor} variant="light" size="sm">
                {badge}
              </Badge>
            )}
          </Group>
          {description && (
            <Text c="dimmed" size="sm" mt="xs">
              {description}
            </Text>
          )}
        </div>
      </Group>
      {actions}
    </Group>
  );

  if (!showCard) {
    return (
      <Stack gap="md">
        {pageHeader}
        {children}
      </Stack>
    );
  }

  return (
    <Card
      shadow="sm"
      padding="sm"
      radius="md"
      withBorder
      style={{
        backgroundColor: 'var(--color-admin-card)',
      }}
    >
      <Stack gap="md">
        {pageHeader}
        {children}
      </Stack>
    </Card>
  );
};

export default AdminPageLayout;
